import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import API_ENDPOINTS from '../ApiEndPoints';
import WorkflowFilterEditor from './WorkflowFilterEditor';

const WorkflowSettingsPanel = ({
  open,
  onClose,
  workflowId,
  workflowTitle,
  stages,
  selectedFilterTid,
  appliedSearch,
  onToggleStage,
  onAdoptOrphans,
  adopting,
  onInfo,
  onError,
  onRefreshBoard,
  onPersistFilterSelection,
  persistingFilter,
}) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reassignFrom, setReassignFrom] = useState(null);
  const [reassignTo, setReassignTo] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const fetchSettings = useCallback(async () => {
    if (!open || !workflowId) {
      return;
    }
    setLoading(true);
    setValidationError(null);
    try {
      const params = {};
      if (selectedFilterTid) {
        params.filter = selectedFilterTid;
      }
      if (appliedSearch) {
        params.search = appliedSearch;
      }
      const { data } = await axios.get(
        API_ENDPOINTS.workflowBoard.settings(workflowId),
        { params }
      );
      setSettings(data);
    } catch (error) {
      onError?.(error?.response?.data?.message || 'Unable to load workflow settings.');
    } finally {
      setLoading(false);
    }
  }, [open, workflowId, selectedFilterTid, appliedSearch, onError]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    fetchSettings();
    return undefined;
  }, [open, fetchSettings]);

  const stageList = useMemo(() => {
    if (!Array.isArray(stages)) {
      return [];
    }
    return [...stages].sort((a, b) => (a.rank || 0) - (b.rank || 0));
  }, [stages]);

  const statusRows = useMemo(() => {
    if (!settings) {
      return [];
    }
    const counts = settings.status_counts || {};
    const options = settings.status_options || [];
    const rows = options.map((option) => ({
      label: option.label || option.value || '(Blank)',
      value: option.value,
      count: counts[option.value] || 0,
    }));
    Object.keys(counts || {}).forEach((value) => {
      if (!rows.find((row) => row.value === value)) {
        rows.push({
          label: value || '(Blank)',
          value,
          count: counts[value] || 0,
        });
      }
    });
    return rows;
  }, [settings]);

  const resetReassign = () => {
    setReassignFrom(null);
    setReassignTo('');
    setValidationError(null);
  };

  const handleReassignSubmit = async () => {
    if (!workflowId || !reassignFrom) {
      return;
    }
    if (!reassignTo) {
      setValidationError('Select a target status to continue.');
      return;
    }
    setValidationError(null);
    setReassigning(true);
    try {
      const { data } = await axios.post(
        API_ENDPOINTS.workflowBoard.reassignStatuses(workflowId),
        {
          from: reassignFrom,
          to: reassignTo,
        }
      );
      onInfo?.(`${data.updated} record${data.updated === 1 ? '' : 's'} moved to ${reassignTo}.`);
      resetReassign();
      await fetchSettings();
      onRefreshBoard?.();
    } catch (error) {
      onError?.(error?.response?.data?.message || 'Unable to reassign statuses.');
    } finally {
      setReassigning(false);
    }
  };

  if (!open) {
    return null;
  }

  const panelTitle = workflowTitle ? `${workflowTitle} settings` : 'Workflow settings';

  return (
    <div className="workflow-settings-panel">
      <div className="workflow-settings-panel__backdrop" onClick={onClose} />
      <div className="workflow-settings-panel__content">
        <div className="workflow-settings-panel__header">
          <div>
            <h4>{panelTitle}</h4>
            <p className="text-muted">Adjust stage visibility and keep statuses healthy.</p>
          </div>
          <button type="button" className="btn btn-link" onClick={() => { resetReassign(); onClose(); }}>
            Close
          </button>
        </div>
        <div className="workflow-settings-panel__body">
          {loading && (
            <div className="alert alert-info">
              Loading settings…
            </div>
          )}

          <section className="workflow-settings-section">
            <div className="workflow-settings-section__header">
              <h5>Stages</h5>
              <p>Toggle which stages appear on the board.</p>
            </div>
            {stageList.length === 0 && <p className="text-muted">No stages configured.</p>}
            {stageList.length > 0 && (
              <ul className="workflow-settings-stage-list">
                {stageList.map((stage) => (
                  <li key={stage.tid} className="workflow-settings-stage-row">
                    <div>
                      <div className="workflow-settings-stage-title">{stage.title}</div>
                      {stage.description && (
                        <div className="workflow-settings-stage-description">{stage.description}</div>
                      )}
                    </div>
                    <div className="workflow-settings-stage-actions">
                      <span className="workflow-settings-stage-count">{stage.count ?? 0} open</span>
                      <button
                        type="button"
                        className={`btn btn-xs ${stage.hidden ? 'btn-default' : 'btn-primary'}`}
                        onClick={() => onToggleStage(stage, !stage.hidden)}
                      >
                        {stage.hidden ? 'Show' : 'Hide'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <WorkflowFilterEditor
            open={open}
            workflowId={workflowId}
            selectedFilterTid={selectedFilterTid}
            onPersistFilterSelection={onPersistFilterSelection}
            persistingFilter={persistingFilter}
            onInfo={onInfo}
            onError={onError}
            onRefreshBoard={onRefreshBoard}
          />

          <section className="workflow-settings-section">
            <div className="workflow-settings-section__header">
              <h5>Status health</h5>
              <p>Resolve mismatched or legacy statuses by reassigning them.</p>
            </div>
            {validationError && (
              <div className="alert alert-warning">{validationError}</div>
            )}
            {statusRows.length === 0 && !loading && (
              <p className="text-muted">No status counts available.</p>
            )}
            {statusRows.length > 0 && (
              <div className="workflow-settings-status-list">
                {statusRows.map((row) => {
                  const rowKey = row.value ?? row.label ?? 'blank';
                  const canReassign = row.value !== null && typeof row.value !== 'undefined';
                  const isActive = canReassign && reassignFrom === row.value;
                  const targetOptions = statusRows.filter(
                    (option) => option.value !== row.value && option.value !== null && typeof option.value !== 'undefined'
                  );

                  return (
                    <div className="workflow-settings-status-row" key={rowKey}>
                      <div>
                        <div className="workflow-settings-status-label">{row.label}</div>
                        <div className="workflow-settings-status-count">{row.count} record{row.count === 1 ? '' : 's'}</div>
                      </div>
                      <div>
                        {isActive ? (
                          <div className="workflow-settings-status-reassign">
                            <select
                              className="form-control input-sm"
                              value={reassignTo}
                              onChange={(event) => setReassignTo(event.target.value)}
                              disabled={reassigning}
                            >
                              <option value="">Move records to…</option>
                              {targetOptions.map((option) => (
                                <option key={option.value || option.label} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <div className="workflow-settings-status-reassign-actions">
                              <button
                                type="button"
                                className="btn btn-primary btn-xs"
                                onClick={handleReassignSubmit}
                                disabled={reassigning || !reassignTo}
                              >
                                {reassigning ? 'Working…' : 'Apply'}
                              </button>
                              <button
                                type="button"
                                className="btn btn-link btn-xs"
                                onClick={resetReassign}
                                disabled={reassigning}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-default btn-xs"
                            disabled={!canReassign || row.count === 0}
                            onClick={() => {
                              if (!canReassign || row.count === 0) {
                                return;
                              }
                              setReassignFrom(row.value);
                              setReassignTo('');
                            }}
                          >
                            Reassign
                          </button>
                        )}
                        {!canReassign && (
                          <div className="workflow-settings-status-help text-muted">
                            Set a status value for these records before reassigning.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="workflow-settings-section">
            <div className="workflow-settings-section__header">
              <h5>Maintenance</h5>
              <p>Keep your board tidy by scooping up orphans.</p>
            </div>
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={onAdoptOrphans}
              disabled={adopting}
            >
              {adopting ? 'Adopting…' : 'Adopt orphaned records'}
            </button>
          </section>
        </div>
      </div>
    </div>
  );
};

WorkflowSettingsPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workflowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  workflowTitle: PropTypes.string,
  stages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    tid: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    hidden: PropTypes.bool,
    count: PropTypes.number,
    rank: PropTypes.number,
  })),
  selectedFilterTid: PropTypes.string,
  appliedSearch: PropTypes.string,
  onToggleStage: PropTypes.func.isRequired,
  onAdoptOrphans: PropTypes.func.isRequired,
  adopting: PropTypes.bool,
  onInfo: PropTypes.func,
  onError: PropTypes.func,
  onRefreshBoard: PropTypes.func,
  onPersistFilterSelection: PropTypes.func,
  persistingFilter: PropTypes.bool,
};

WorkflowSettingsPanel.defaultProps = {
  workflowTitle: '',
  stages: [],
  selectedFilterTid: null,
  appliedSearch: '',
  adopting: false,
  onInfo: null,
  onError: null,
  onRefreshBoard: null,
  onPersistFilterSelection: null,
  persistingFilter: false,
};

export default WorkflowSettingsPanel;
